import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Loader from '../../components/Loader/Loader';
import Error from '../../components/Error/Error';
import { getNewsArticle } from '../../shared/repos/graphql/news';
import endpoints from '../../shared/constants/endpoints';
import {
  NEWSROOM_TITLE, articlePath, buildShareLinks, formatNewsDate, sanitizeNewsHtml,
} from './helpers';
import styles from './News.module.css';

const NewsArticle = () => {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, error: false, article: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: false, article: null });
    getNewsArticle(slug)
      .then(({ data }) => {
        if (!cancelled) setState({ loading: false, error: false, article: data.newsArticle });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, error: true, article: null });
      });
    window.scrollTo(0, 0);
    return () => { cancelled = true; };
  }, [slug]);

  const { loading, error, article } = state;
  const bodyHtml = useMemo(() => sanitizeNewsHtml(article?.body), [article]);

  if (loading) {
    return (
      <div className={styles.pageWrapper} data-region="secondary-page" data-page="NewsArticle">
        <Helmet><title>{NEWSROOM_TITLE}</title></Helmet>
        <div className={styles.pageContainer} data-part="content-surface">
          <Loader id="loader" />
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <>
        <Helmet><title>{NEWSROOM_TITLE}</title></Helmet>
        <Error errorType={error ? '400' : '404'} />
      </>
    );
  }

  const url = `${window.location.origin}${articlePath(article.slug)}`;
  const pageTitle = `${article.title} | Insomnia Cookies Newsroom`;

  return (
    <div className={styles.pageWrapper} data-region="secondary-page" data-page="NewsArticle">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={article.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:url" content={url} />
        {article.image && <meta property="og:image" content={article.image} />}
        <meta name="twitter:card" content={article.image ? 'summary_large_image' : 'summary'} />
        {article.publishedAt && <meta property="article:published_time" content={article.publishedAt} />}
      </Helmet>
      <article className={`${styles.pageContainer} text-dark dark:text-white`} data-part="content-surface">
        <Link to={endpoints.news} className="inline-block mb-8 font-montserratBold hover:underline" data-part="news-back">
          ← Newsroom
        </Link>
        <h1 className={styles.articleTitle}>{article.title}</h1>
        <time dateTime={article.publishedAt} data-part="news-date" className="block mb-8 font-montserratMedium text-darkElevationPrimary dark:text-darkElevationSecondary">
          {formatNewsDate(article.publishedAt)}
        </time>
        {article.image && (
          <img src={article.image} alt="" className="w-full rounded-xl mb-10" />
        )}
        <div
          className={styles.articleBody}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />
        <div className={`${styles.shareBar} flex flex-wrap items-center gap-4`} data-part="news-share">
          <span className="font-montserratBold">Share</span>
          {buildShareLinks(url, article.title).map(link => (
            <a
              key={link.label}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              data-part="news-pill"
              className="px-4 py-1 rounded-full border-2 border-dark dark:border-white font-montserratMedium hover:bg-dark hover:text-white"
            >
              {link.label}
            </a>
          ))}
        </div>
      </article>
    </div>
  );
};

export default NewsArticle;
